import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Heart, 
  Clock, 
  AlertCircle,
  Send,
  Check,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const FollowUpsPage = () => {
  const [data, setData] = useState({ due_today: [], missed: [], upcoming: [] });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchFollowUps();
  }, []);

  const fetchFollowUps = async () => {
    try {
      const response = await apiClient.get('/follow-ups');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (visitId) => {
    setSending({ ...sending, [visitId]: true });
    try {
      const response = await apiClient.post(`/follow-ups/${visitId}/send-message`);
      toast.success('Care message sent!');
      if (response.data.result?.status === 'stub_sent') {
        toast.info('(Stub mode - message logged but not actually sent)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending({ ...sending, [visitId]: false });
    }
  };

  const markComplete = async (visitId) => {
    try {
      await apiClient.put(`/follow-ups/${visitId}/complete`);
      toast.success('Follow-up marked as complete');
      fetchFollowUps();
    } catch (error) {
      toast.error('Failed to update follow-up');
    }
  };

  const PatientAvatar = ({ photo, name }) => (
    photo ? (
      <img 
        src={`data:image/png;base64,${photo}`} 
        alt={name}
        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
      />
    ) : (
      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
        {name?.charAt(0) || '?'}
      </div>
    )
  );

  const FollowUpCard = ({ item, showSendButton = true, isMissed = false }) => (
    <Card 
      className="card-hover cursor-pointer"
      onClick={() => navigate(`/patients/${item.patient_id}`)}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <PatientAvatar photo={item.patient_photo} name={item.patient_name} />
          
          <div className="flex-1 min-w-0">
            <h3 className="font-medium truncate">{item.patient_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="w-3 h-3 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {item.follow_up_date}
              </span>
              {isMissed && (
                <Badge variant="destructive" className="text-xs">Missed</Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {showSendButton && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => sendMessage(item.visit_id)}
                disabled={sending[item.visit_id]}
                data-testid={`send-message-${item.visit_id}`}
              >
                {sending[item.visit_id] ? (
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => markComplete(item.visit_id)}
              data-testid={`mark-complete-${item.visit_id}`}
            >
              <Check className="w-4 h-4" />
            </Button>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalFollowUps = data.due_today.length + data.missed.length + data.upcoming.length;

  return (
    <div className="space-y-6" data-testid="follow-ups-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
          <Heart className="w-6 h-6 text-primary" strokeWidth={1.5} />
          Follow-up Care
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Stay connected with your patients
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-600">{data.due_today.length}</p>
            <p className="text-xs text-muted-foreground">Due Today</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-600">{data.missed.length}</p>
            <p className="text-xs text-muted-foreground">Missed</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4 text-center">
            <Calendar className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-600">{data.upcoming.length}</p>
            <p className="text-xs text-muted-foreground">Upcoming</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      {totalFollowUps === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Heart className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No pending follow-ups</p>
            <p className="text-sm text-muted-foreground mt-1">
              Set follow-up dates when recording visits
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="today">
          <TabsList>
            <TabsTrigger value="today">
              Due Today ({data.due_today.length})
            </TabsTrigger>
            <TabsTrigger value="missed" className="text-destructive">
              Missed ({data.missed.length})
            </TabsTrigger>
            <TabsTrigger value="upcoming">
              Upcoming ({data.upcoming.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-3 mt-4">
            {data.due_today.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No follow-ups due today</p>
                </CardContent>
              </Card>
            ) : (
              data.due_today.map((item, index) => (
                <div key={item.visit_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                  <FollowUpCard item={item} />
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="missed" className="space-y-3 mt-4">
            {data.missed.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No missed follow-ups</p>
                </CardContent>
              </Card>
            ) : (
              data.missed.map((item, index) => (
                <div key={item.visit_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                  <FollowUpCard item={item} isMissed />
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="upcoming" className="space-y-3 mt-4">
            {data.upcoming.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No upcoming follow-ups</p>
                </CardContent>
              </Card>
            ) : (
              data.upcoming.map((item, index) => (
                <div key={item.visit_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                  <FollowUpCard item={item} showSendButton={false} />
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Help text */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4">
          <p className="text-sm">
            <strong>Tip:</strong> Send care messages to patients who missed their follow-ups. 
            Messages are sent via email (SMS coming soon). Use a friendly tone - 
            "We just want to check how you're feeling."
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FollowUpsPage;
